import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  type Period,
  formatPeriodLabel,
  shiftPeriod,
  currentPeriod,
} from '@/lib/format';
import type { FilterMode } from '@/hooks/useFilters';

const MONTH_NAMES_SHORT = [
  'jan.', 'fev.', 'mar.', 'abr.', 'mai.', 'jun.',
  'jul.', 'ago.', 'set.', 'out.', 'nov.', 'dez.',
] as const;

const formatRangeDate = (date: Date) =>
  date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

interface MonthGridProps {
  pending: Period;
  onSelect: (period: Period) => void;
}

const MonthGrid: React.FC<MonthGridProps> = ({ pending, onSelect }) => {
  const [year, setYear] = useState(pending.year);
  const today = currentPeriod();

  return (
    <div className="space-y-3 p-1">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setYear((y) => y - 1)}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          aria-label="Ano anterior"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-semibold tabular-nums">{year}</span>
        <button
          type="button"
          onClick={() => setYear((y) => y + 1)}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          aria-label="Próximo ano"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {MONTH_NAMES_SHORT.map((name, idx) => {
          const isPending = pending.year === year && pending.month === idx;
          const isCurrent = today.year === year && today.month === idx;
          return (
            <button
              key={name}
              type="button"
              onClick={() => onSelect({ year, month: idx })}
              className={cn(
                'h-9 rounded-md text-sm font-medium transition-colors tabular-nums',
                isPending
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                  : isCurrent
                  ? 'border border-primary/40 text-foreground hover:bg-accent'
                  : 'text-foreground hover:bg-accent',
              )}
            >
              {name}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export interface PeriodFilterProps {
  mode: FilterMode;
  pendingPeriod: Period;
  pendingFrom: Date;
  pendingTo: Date;
  /**
   * Called when the user picks a month (stepper click or month-grid click).
   * Implementations apply this immediately to the URL — stepping is atomic.
   */
  onMonthPick: (period: Period) => void;
  /**
   * Called when the user picks a range. Updates pending only; commit goes via Aplicar.
   */
  onRangeChange: (from: Date, to: Date) => void;
}

export const PeriodFilter: React.FC<PeriodFilterProps> = ({
  mode,
  pendingPeriod,
  pendingFrom,
  pendingTo,
  onMonthPick,
  onRangeChange,
}) => {
  const [monthOpen, setMonthOpen] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);

  const handleStep = (delta: number) => {
    onMonthPick(shiftPeriod(pendingPeriod, delta));
  };

  const handleMonthPick = (period: Period) => {
    onMonthPick(period);
    setMonthOpen(false);
  };

  const handleRangeSelect = (range: DateRange | undefined) => {
    if (!range?.from) return;
    if (range.to) {
      onRangeChange(range.from, range.to);
    } else {
      onRangeChange(range.from, range.from);
    }
  };

  if (mode === 'month') {
    return (
      <div className="inline-flex items-center rounded-md border border-input bg-background">
        <button
          type="button"
          onClick={() => handleStep(-1)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-l-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          aria-label="Mês anterior"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <Popover open={monthOpen} onOpenChange={setMonthOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="h-9 min-w-[120px] px-3 text-sm font-semibold tabular-nums border-x border-input text-foreground transition-colors hover:bg-accent"
              aria-label="Selecionar mês"
            >
              {formatPeriodLabel(pendingPeriod)}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[260px] p-2">
            <MonthGrid pending={pendingPeriod} onSelect={handleMonthPick} />
          </PopoverContent>
        </Popover>
        <button
          type="button"
          onClick={() => handleStep(1)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-r-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          aria-label="Próximo mês"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <Popover open={rangeOpen} onOpenChange={setRangeOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-9 justify-start text-left font-medium tabular-nums"
        >
          {formatRangeDate(pendingFrom)} <span className="mx-1 text-muted-foreground">–</span>{' '}
          {formatRangeDate(pendingTo)}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <Calendar
          mode="range"
          numberOfMonths={1}
          selected={{ from: pendingFrom, to: pendingTo }}
          onSelect={handleRangeSelect}
          defaultMonth={pendingFrom}
          initialFocus
          className="pointer-events-auto"
        />
      </PopoverContent>
    </Popover>
  );
};

PeriodFilter.displayName = 'PeriodFilter';
